import { memo } from "react";
import { styled, Tooltip } from "@mui/material";
import MxTypography from "@rf-l1/MxTypography";
import MenuOptionTableDetail from "./MenuOptionTableDetail";
import { O_CURRENCY } from "@/src-refactor/modules/shared/model/const/currency.conts";
import MoneyFormat from "@/src-refactor/modules/shared/helpers/moneyFormat.helper";
import { PROPERTIES_STATUS } from "@/shared/models/const/properties/properties-status.const";
import { C_REALESTATE_TYPES_PROJECT } from "@/src-refactor/modules/shared/model/const/property/real-estate-type.const";
import { IUnitFromDetail } from "@rf/property/module/adapters/detail/prepareDetailPropertyBuildings";

interface UnitRowProps {
  unit: IUnitFromDetail;
  visibleColumns: any;
  realestateId: number;
  onCopyId: (id: number) => void;
}

const UnitRow = memo(
  ({ unit, visibleColumns, realestateId, onCopyId }: UnitRowProps) => {
    const getStatusColor = (status: string) => {
      const statusObj = Object.values(PROPERTIES_STATUS).find(
        s => s.name === status
      );
      return statusObj?.color || "var(--m-color-blue-03)";
    };

    return (
      <HeaderColumn realestateId={realestateId}>
        <Section style={{ position: "relative" }}>
          <ContentStatus statusColor={getStatusColor(unit.status)}>
            {unit.status}
          </ContentStatus>
          <MxTypography
            variant="overline"
            fontWeight={700}
            onClick={() => onCopyId(unit.id)}
            sx={{ cursor: "pointer", "&:hover": { color: "primary.main" } }}
          >
            {unit.id}
          </MxTypography>
        </Section>

        {visibleColumns.name && (
          <RowName>
            <Tooltip title={unit.name} placement="top">
              <MxTypography variant="caption" fontWeight={700} lines={2}>
                {unit.name}
              </MxTypography>
            </Tooltip>
          </RowName>
        )}

        {visibleColumns.bedrooms && (
          <Section>
            <MxTypography variant="caption" fontWeight={700}>
              {unit.bedrooms}
            </MxTypography>
          </Section>
        )}

        {visibleColumns.bathrooms && (
          <Section>
            <MxTypography variant="caption" fontWeight={700}>
              {unit.bathrooms}
            </MxTypography>
          </Section>
        )}

        {visibleColumns.parking_spots && (
          <Section>
            <MxTypography variant="caption" fontWeight={700}>
              {unit.parking}
            </MxTypography>
          </Section>
        )}

        {visibleColumns.floor_level && (
          <Section>
            <MxTypography variant="caption" fontWeight={700}>
              {unit.floorLevel}
            </MxTypography>
          </Section>
        )}

        {visibleColumns.sqm_land && (
          <Section>
            <MxTypography variant="caption" fontWeight={700}>
              {unit.sqmLand ? unit.sqmLand.toFixed(2) : "0.00"}
            </MxTypography>
          </Section>
        )}

        {visibleColumns.sqm_construction && (
          <Section>
            <MxTypography variant="caption" fontWeight={700}>
              {unit.sqmConstruction ? unit.sqmConstruction.toFixed(2) : "0.00"}
            </MxTypography>
          </Section>
        )}

        {visibleColumns.price && (
          <Section style={{ position: "relative" }}>
            <ContentOptions>
              <MenuOptionTableDetail unit={unit as any} />
            </ContentOptions>
            <MxTypography variant="caption" fontWeight={700}>
              {O_CURRENCY[unit.currency as keyof typeof O_CURRENCY]?.label ||
                unit.currency}{" "}
              {MoneyFormat(unit.price)}
            </MxTypography>
          </Section>
        )}
      </HeaderColumn>
    );
  }
);

UnitRow.displayName = "UnitRow";

export default UnitRow;

// Styled Components
const ContentOptions = styled("div")(({ theme }) => ({
  position: "absolute",
  zIndex: 1000,
  right: "-22px",
  top: "0px"
}));

const ContentStatus = styled("div")<{ statusColor?: string }>(
  ({ theme, statusColor }) => ({
    position: "absolute",
    zIndex: 1000,
    left: "-18px",
    top: "0px",
    backgroundColor: statusColor || "var(--m-color-blue-03)",
    color: "white",
    fontSize: "5.5px",
    textTransform: "uppercase",
    letterSpacing: "1px",
    fontWeight: 600,
    borderBottomRightRadius: "5px",
    borderTopRightRadius: "5px",
    height: "55px",
    width: "17px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    whiteSpace: "nowrap",
    writingMode: "vertical-rl",
    transform: "rotate(180deg)",
    borderBottom: "1px solid white",
    fontFamily: "Inter",
    overflow: "hidden",
    textOverflow: "ellipsis"
  })
);

const HeaderColumn = styled("div")<{ realestateId: number }>(
  ({ theme, realestateId }) => ({
    width: "100%",
    flexDirection: "row",
    height: "55px",
    backgroundColor: "#f5f5f5",
    display: "grid",
    gridTemplateColumns: (() => {
      if (realestateId === C_REALESTATE_TYPES_PROJECT.LAND.id) {
        return "0.5fr  repeat(3, 0.9fr)";
      } else if (
        realestateId === C_REALESTATE_TYPES_PROJECT.COMERCIAL_SITE.id ||
        realestateId === C_REALESTATE_TYPES_PROJECT.INDUSTRIAL_SITE.id ||
        realestateId === C_REALESTATE_TYPES_PROJECT.HOUSE.id
      ) {
        return "0.45fr 0.8fr repeat(4, 0.25fr)  repeat(2, 0.5fr)";
      } else if (realestateId === C_REALESTATE_TYPES_PROJECT.APARTMENT.id) {
        return "0.45fr 0.8fr repeat(5, 0.25fr)  repeat(2, 0.5fr)";
      }
    })(),
    borderLeft: "1px solid var(--m-color-blue-03)",
    "& > div": {
      borderRight: "1px solid var(--m-color-blue-03)",
      borderBottom: "1px solid var(--m-color-blue-03)",
      padding: "0 5px"
    },
    "& > div:last-of-type": {
      borderRight: "none"
    }
  })
);

const RowName = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyItems: "center",
  justifyContent: "center",
  backgroundColor: "white",
  height: "auto",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "normal",
  wordBreak: "break-word",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  fontFamily: "Inter",
  fontSize: "12px",
  fontWeight: 700,
  lineHeight: "19px",
  letterSpacing: "0em",
  textAlign: "left",
  color: "var(--m-color-gray-02)"
}));

const Section = styled("div")<{ duo?: boolean }>(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  cursor: "pointer",
  "& > .error-icon": {
    position: "absolute",
    right: "10px",
    fill: "#f6ca05"
  },
  height: "55px",
  backgroundColor: "white",
  color: "var(--m-color-gray-02)",
  fontFamily: "Inter",
  fontSize: "12px",
  fontWeight: 700,
  "*": {
    border: "none",
    fontFamily: "Inter",
    fontSize: "10px",
    fontWeight: 700,
    lineHeight: "19px",
    letterSpacing: "0em",
    textAlign: "left",
    color: "#949ca1"
  }
}));
